import { Info, ArrowDownUp, History, CircleMinus } from 'lucide-react'
import { MoneySend } from 'iconsax-react'
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from '../../../components/ui/card'
import { formatMoneyValue } from '../../../../app/utils/formatMoneyValue'
import { format } from 'date-fns'
import { RemoveSeller } from './RemoveSeller'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip'
import { useBlockAndUnblockController } from '../../../components/BlockAndUnBlock/useBlockAndUnblockController'
import { BasicAvatar } from '../../../components/BasicAvatar'
import { useAuth } from '@/app/hooks/useAuth'

type SellerCardProps = {
  _id: string
  name: string
  profileImageUrl: string
  isActive: boolean
  team?: {
    _id?: string
    name?: string
  }
  transactions: {
    count: number
    total: number
    lastMonth: number
    lastTransaction: string
  }
  handleSellerDetailsPage: (id: string) => void
  isRemove: boolean
}

export const SellerCard = ({
  _id,
  name,
  profileImageUrl,
  team,
  transactions,
  handleSellerDetailsPage,
  isRemove,
  isActive,
}: SellerCardProps) => {
  const { handleEnableUser } = useBlockAndUnblockController()
  const { isAdmin } = useAuth()

  return (
    <Card
      onClick={() => handleSellerDetailsPage(_id)}
      className={`group relative cursor-pointer border-[1px] border-[#E5E5E5] p-5 ${!isActive && 'border-error'}`}
    >
      {!isActive && (
        <>
          <div className="absolute inset-0 z-10 rounded-2xl bg-[#00000005] backdrop-blur-[1px]"></div>
          <div className="absolute left-1/2 top-1/2 z-50 block -translate-x-1/2 -translate-y-1/2 transform group-hover:hidden">
            <div className="flex h-12 w-40 items-center justify-center gap-2 rounded-full border-[1px] border-error bg-error">
              <CircleMinus color="white" />
              <p className="font-medium text-white">Bloqueado</p>
            </div>
          </div>
          <div
            onClick={(event) => event.stopPropagation()}
            className="absolute left-1/2 top-1/2 z-50 hidden -translate-x-1/2 -translate-y-1/2 transform group-hover:block"
          >
            <div
              onClick={() => handleEnableUser(_id)}
              className="flex h-12 w-80 items-center justify-center gap-2 rounded-full border-[1px] border-[#E5E5E5] bg-white"
            >
              <CircleMinus />
              <p className="font-medium">
                Desbloquear &quot;<b>{name}</b>&quot;?
              </p>
            </div>
          </div>
        </>
      )}
      <CardHeader className="p-0">
        <div className="flex flex-col gap-4">
          <div className="flex justify-between">
            <div className="flex items-center gap-2">
              <BasicAvatar name={name} profileImageUrl={profileImageUrl} />
              <p className="font-semibold">{name}</p>
            </div>
            {isAdmin && isRemove && (
              <div className="hidden group-hover:block">
                <RemoveSeller id={_id} />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <p className="text-xs font-medium text-[#747474]">
              {transactions.lastMonth ? transactions.lastMonth : 0} saques no
              último mês
            </p>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info size={16} color="black" />
                </TooltipTrigger>
                <TooltipContent className="w-52 rounded-xl bg-black">
                  <p className="text-xs text-white">
                    O <b>&quot;SAQUE&quot;</b> é a transferência dos seus ganhos
                    de comissão para sua conta bancária. Este processo pode
                    levar até 8 dias para ser aprovado.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex items-center gap-2">
            {team ? (
              <>
                <i className="fa-solid fa-people-group"></i>
                <p className="text-sm">{team?.name}</p>
              </>
            ) : (
              <>
                <i className="fa-solid fa-people-group"></i>
                <p className="text-sm">Sem Equipe</p>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="mt-4 flex items-center gap-2 p-0">
        <div className="relative h-28 w-full rounded-lg border-[1px] border-gray02 px-3 py-2">
          <div className="flex justify-end">
            <div className="absolute right-3 top-2 h-4 p-0">
              <Info size={16} color="black" />
            </div>
          </div>
          <div className="mt-4 flex flex-col items-center justify-center">
            <MoneySend size="24" className="mb-2" />
            <p className="text-xs font-semibold">
              {transactions.total
                ? formatMoneyValue(transactions.total)
                : formatMoneyValue(0)}
            </p>
            <p className="text-center text-[10px] font-medium">
              Total em Propostas pagas
            </p>
          </div>
        </div>
        <div className="relative h-28 w-full rounded-lg border-[1px] border-gray02 px-3 py-2">
          <div className="flex justify-end">
            <div className="absolute right-3 top-2 h-4 p-0">
              <Info size={16} color="black" />
            </div>
          </div>
          <div className="mt-4 flex flex-col items-center justify-center">
            <ArrowDownUp size={24} className="mb-2" />
            <p className="text-xs font-semibold">
              {transactions.count ? transactions.count : 0}
            </p>
            <p className="text-center text-[10px] font-medium">
              Quantidade de Propostas Pagas
            </p>
          </div>
        </div>
      </CardContent>
      <CardFooter className="mt-5 p-0">
        {transactions.lastTransaction ? (
          <div className="flex w-full items-center justify-center gap-1">
            <History size={22} />
            <p className="text-xs font-medium">Ultima movimentação: </p>
            <p className="text-xs font-medium">
              {' '}
              {transactions.lastTransaction
                ? format(transactions.lastTransaction, 'dd/MM/yyyy, HH:mm')
                : null}
            </p>
          </div>
        ) : null}
      </CardFooter>
    </Card>
  )
}
